import { useCallback, useEffect, useMemo, useState } from "react";
import { useNavigate } from "react-router-dom";
import { toast } from "sonner";
import { Truck, Plus, Search, PackageCheck, Clock, AlertTriangle } from "lucide-react";
import { api, apiError } from "@/lib/api";
import { inr, fmtDate, titleCase } from "@/lib/format";
import { useCanEdit } from "@/context/AuthContext";
import { CardList, DataCard, TableWrap } from "@/components/DataCards";
import Pagination, { usePagedList, downloadFile } from "@/components/Pagination";
import { PortButtons } from "@/components/DataPorting";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";

const STATUS_TONE = {
  DRAFT: "bg-slate-100 text-slate-600",
  ISSUED: "bg-blue-50 text-blue-700",
  INVOICED: "bg-emerald-50 text-emerald-700",
  CANCELLED: "bg-rose-50 text-rose-700",
};

const Chip = ({ status }) => (
  <span className={`inline-block rounded px-1.5 py-0.5 text-[10px] font-semibold ${
    STATUS_TONE[status] || "bg-slate-100 text-slate-600"}`}>
    {titleCase(status || "")}
  </span>
);

const Tile = ({ icon: Icon, label, value, sub, tone = "text-blue-500", testid }) => (
  <div className="rounded-lg border border-slate-200 bg-white p-4" data-testid={testid}>
    <div className="flex items-center gap-2 text-[11px] uppercase tracking-wide text-slate-500">
      <Icon size={13} className={tone} /> {label}
    </div>
    <div className="mt-2 font-heading text-2xl font-bold tabular-nums">{value}</div>
    {sub && <div className="text-[11px] text-slate-500">{sub}</div>}
  </div>
);

export default function Challans() {
  const canEdit = useCanEdit();
  const navigate = useNavigate();
  const [summary, setSummary] = useState(null);
  const [q, setQ] = useState("");
  const [status, setStatus] = useState("ALL");
  const [purpose, setPurpose] = useState("ALL");
  const [purposes, setPurposes] = useState([]);

  const query = useMemo(() => ({
    q: q || undefined,
    status: status === "ALL" ? undefined : status,
    purpose: purpose === "ALL" ? undefined : purpose,
  }), [q, status, purpose]);

  const { rows, total, page, setPage, size, setSize, loading, reload } =
    usePagedList("/challans", query);

  const loadSummary = useCallback(() => {
    api.get("/challans/summary").then(({ data }) => setSummary(data)).catch(() => {});
  }, []);
  useEffect(() => { loadSummary(); }, [loadSummary]);
  useEffect(() => {
    api.get("/challans/purposes").then(({ data }) => setPurposes(data)).catch(() => {});
  }, []);

  const refresh = () => { reload(); loadSummary(); };

  const convert = async (row) => {
    try {
      const { data } = await api.post(`/challans/${row.id}/convert`);
      toast.success("Draft invoice created — the goods are already out of stock");
      navigate(`/invoices/${data.document_id}/edit`);
    } catch (e) {
      toast.error(apiError(e));
    }
  };

  const cancel = async (row) => {
    const reason = window.prompt(`Cancel ${row.number || "this draft"}? Reason (optional):`);
    if (reason === null) return;
    try {
      await api.post(`/challans/${row.id}/cancel`, { reason });
      toast.success(row.reduces_stock ? "Cancelled — goods returned to stock" : "Cancelled");
      refresh();
    } catch (e) {
      toast.error(apiError(e));
    }
  };

  const actionsFor = (row) => (
    <>
      <button className="text-xs text-blue-700 hover:text-blue-900 transition-colors duration-150"
        onClick={() => downloadFile(`/challans/${row.id}/pdf`, {}, "challan.pdf")}
        data-testid={`challan-pdf-${row.id}`}>PDF</button>
      {canEdit && row.status === "DRAFT" && (
        <button className="text-xs text-slate-500 hover:text-slate-800 transition-colors duration-150"
          onClick={() => navigate(`/challans/${row.id}/edit`)}
          data-testid={`challan-edit-${row.id}`}>Edit</button>
      )}
      {canEdit && row.status === "DRAFT" && (
        <button className="text-xs font-medium text-blue-700 hover:text-blue-900 transition-colors duration-150"
          onClick={async () => {
            try { await api.post(`/challans/${row.id}/issue`); toast.success("Issued"); refresh(); }
            catch (e) { toast.error(apiError(e)); }
          }} data-testid={`challan-issue-${row.id}`}>Issue</button>
      )}
      {canEdit && row.status === "ISSUED" && (
        <button className="text-xs font-medium text-violet-700 hover:text-violet-900 transition-colors duration-150"
          onClick={() => convert(row)} data-testid={`challan-convert-${row.id}`}>
          Raise invoice
        </button>
      )}
      {canEdit && row.status !== "INVOICED" && row.status !== "CANCELLED" && (
        <button className="text-xs text-slate-400 hover:text-rose-600 transition-colors duration-150"
          onClick={() => cancel(row)} data-testid={`challan-cancel-${row.id}`}>Cancel</button>
      )}
    </>
  );

  return (
    <div className="space-y-5" data-testid="challans-page">
      <div className="flex flex-wrap items-end justify-between gap-4">
        <div>
          <h1 className="font-heading text-2xl sm:text-3xl font-extrabold tracking-tight">
            Delivery Challans
          </h1>
          <p className="mt-1 text-sm text-slate-500">
            The paper that travels with the goods. Nothing here reaches your GST returns
            until you raise the invoice.
          </p>
        </div>
        <div className="flex flex-wrap items-center gap-2">
          <PortButtons datasetKey="challans" />
          {canEdit && (
            <Button data-testid="new-challan-btn" onClick={() => navigate("/challans/new")}
              className="gap-1.5 rounded-full bg-blue-600 hover:bg-blue-700">
              <Plus size={16} /> New Challan
            </Button>
          )}
        </div>
      </div>

      {summary && (
        <div className="grid grid-cols-2 gap-3 lg:grid-cols-4">
          <Tile icon={Truck} label="Out on challan" value={summary.open.count}
            sub={inr(summary.open.amount)} testid="challan-tile-open" />
          <Tile icon={PackageCheck} label="Awaiting invoice" value={summary.awaiting_invoice}
            tone="text-violet-500" testid="challan-tile-awaiting" />
          <Tile icon={Clock} label="Pending return" value={summary.pending_return}
            sub="job work & approval" tone="text-amber-500" testid="challan-tile-return" />
          <Tile icon={AlertTriangle} label="Overdue return" value={summary.overdue_return}
            tone={summary.overdue_return ? "text-rose-500" : "text-slate-400"}
            testid="challan-tile-overdue" />
        </div>
      )}

      <div className="flex flex-wrap items-center gap-2">
        <div className="relative min-w-[180px] flex-1 sm:max-w-xs">
          <Search size={14} className="absolute left-2.5 top-1/2 -translate-y-1/2 text-slate-400" />
          <Input className="h-9 pl-8" placeholder="Number, customer or vehicle"
            data-testid="challans-search" value={q} onChange={(e) => setQ(e.target.value)} />
        </div>
        <Select value={purpose} onValueChange={setPurpose}>
          <SelectTrigger className="h-9 w-full sm:w-56" data-testid="challans-purpose-filter"><SelectValue /></SelectTrigger>
          <SelectContent>
            <SelectItem value="ALL">Any purpose</SelectItem>
            {purposes.map((p) => <SelectItem key={p.code} value={p.code}>{p.label}</SelectItem>)}
          </SelectContent>
        </Select>
        <Select value={status} onValueChange={setStatus}>
          <SelectTrigger className="h-9 w-full sm:w-40" data-testid="challans-status-filter"><SelectValue /></SelectTrigger>
          <SelectContent>
            <SelectItem value="ALL">Any status</SelectItem>
            <SelectItem value="DRAFT">Draft</SelectItem>
            <SelectItem value="ISSUED">Issued</SelectItem>
            <SelectItem value="INVOICED">Invoiced</SelectItem>
            <SelectItem value="CANCELLED">Cancelled</SelectItem>
          </SelectContent>
        </Select>
      </div>

      <TableWrap>
        <table className="w-full text-sm" data-testid="challans-table">
          <thead className="bg-slate-50 text-[11px] uppercase tracking-wide text-slate-500">
            <tr>
              <th className="px-4 py-2.5 text-left font-semibold">Number</th>
              <th className="px-3 py-2.5 text-left font-semibold">Date</th>
              <th className="px-3 py-2.5 text-left font-semibold">Customer</th>
              <th className="px-3 py-2.5 text-left font-semibold">Purpose</th>
              <th className="px-3 py-2.5 text-left font-semibold">Vehicle</th>
              <th className="px-3 py-2.5 text-right font-semibold">Value</th>
              <th className="px-3 py-2.5 text-left font-semibold">Status</th>
              <th className="px-4 py-2.5 text-right font-semibold"></th>
            </tr>
          </thead>
          <tbody className="divide-y divide-slate-100">
            {rows.map((r) => (
              <tr key={r.id} className="hover:bg-slate-50 transition-colors duration-150"
                data-testid={`challan-row-${r.id}`}>
                <td className="px-4 py-2.5">
                  <div className="font-mono text-xs font-semibold">{r.number || "Draft"}</div>
                  {!r.reduces_stock && (
                    <div className="text-[10px] text-slate-400">stock not reduced</div>
                  )}
                </td>
                <td className="px-3 py-2.5 text-xs text-slate-600 whitespace-nowrap">{fmtDate(r.challan_date)}</td>
                <td className="px-3 py-2.5">{r.party_name}</td>
                <td className="px-3 py-2.5 text-xs text-slate-600">
                  {r.purpose_label}
                  {r.is_overdue_return && (
                    <div className="text-[10px] font-semibold text-rose-700">
                      return overdue {fmtDate(r.expected_return_date)}
                    </div>
                  )}
                </td>
                <td className="px-3 py-2.5 font-mono text-[11px] text-slate-500">{r.vehicle_number || "—"}</td>
                <td className="px-3 py-2.5 text-right font-mono tabular-nums">{inr(r.totals?.grand_total)}</td>
                <td className="px-3 py-2.5"><Chip status={r.status} /></td>
                <td className="px-4 py-2.5">
                  <div className="flex flex-wrap items-center justify-end gap-3">{actionsFor(r)}</div>
                </td>
              </tr>
            ))}
            {!rows.length && !loading && (
              <tr><td colSpan={8} className="px-4 py-16 text-center">
                <Truck size={26} className="mx-auto text-slate-300" />
                <p className="mt-3 text-sm font-medium text-slate-700">No delivery challans yet</p>
                <p className="mt-1 text-xs text-slate-500">
                  Send goods out on a challan when the invoice has to follow, or when the
                  goods are going for job work.
                </p>
              </td></tr>
            )}
          </tbody>
        </table>
      </TableWrap>

      <CardList empty="No delivery challans yet">
        {rows.map((r) => (
          <DataCard key={r.id} testid={`challan-card-${r.id}`}
            title={r.number || "Draft"} subtitle={`${r.party_name} · ${fmtDate(r.challan_date)}`}
            right={<>
              <div className="font-mono text-sm font-bold tabular-nums">{inr(r.totals?.grand_total)}</div>
              <div className="mt-1"><Chip status={r.status} /></div>
            </>}
            rows={[["Purpose", r.purpose_label], ["Vehicle", r.vehicle_number || "—"]]}
            actions={actionsFor(r)} />
        ))}
      </CardList>

      <Pagination total={total} page={page} setPage={setPage} size={size} setSize={setSize}
        loading={loading} testid="challans-pagination" />
    </div>
  );
}
